<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import auth from "@/api/auth/auth";
import { useAuthStore } from "@/app/stores/authStore";
import { useUiStore } from "@/app/stores/uiStore";
import HeaderTitle from "@/components/layout/HeaderTitle";
import Notification from "@/utils/plugins/NotificationService";
import { getPasswordErrors } from "@/utils/passwordValidation";

const authStore = useAuthStore();
const uiStore = useUiStore();
const route = useRoute();

const currentPassword = ref("");
const password = ref("");
const repeatPassword = ref("");
const showCurrentPassword = ref(false);
const showPassword = ref(false);
const showRepeatPassword = ref(false);
const submitted = ref(false);
const isMobile = ref(window.innerWidth <= 767);
const passwordErrors = computed(() => getPasswordErrors(password.value));
const pageTitle = computed(() => (isMobile.value ? "Seguridad" : "Mi cuenta · Seguridad"));

function updateViewport() {
  isMobile.value = window.innerWidth <= 767;
}

function initDataApplication() {
  if (route.name !== "login") {
    authStore.initialiseStore();
    if (!authStore.getUserLogged) {
      auth.logout();
    }
  }
}

function samePassword(passwordValue, repeatPasswordValue) {
  return passwordValue === repeatPasswordValue;
}

function submit() {
  submitted.value = true;

  if (passwordErrors.value.length) {
    return;
  }

  if (!samePassword(password.value, repeatPassword.value)) {
    Notification.addNotification("Las contraseñas deben ser iguales", "warning", 6000);
    return;
  }

  updatePassword();
}

async function updatePassword() {
  uiStore.startLoading();

  const user = {
    currentPassword: currentPassword.value,
    password: password.value,
    repeatPassword: repeatPassword.value,
  };

  await auth.changePassword(user)
    .then(() => {
      Notification.addNotification("Contraseña actualizada correctamente", "success", 6000);
      auth.logout();
    })
    .catch((err) => {
      const message = err?.response?.data?.error ?? "Error al actualizar la contraseña";
      Notification.addNotification(message, "error", 6000);
    })
    .finally(() => {
      uiStore.stopLoading();
    });
}

function logout() {
  auth.logout();
}

watch(
  () => route.name,
  (to, from) => {
    if (from === "login" && to !== "login") {
      initDataApplication();
    }
  }
);

onMounted(() => {
  uiStore.stopLoading();
  initDataApplication();
  window.addEventListener("resize", updateViewport);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", updateViewport);
});
</script>

<template>
  <section class="account-security animated fadeIn fast">
    <HeaderTitle :title="pageTitle" />

    <div class="account-security__content">
      <article class="change-password-card" aria-labelledby="change-password-title">
        <div class="change-password-card__heading">
          <svg class="change-password-card__icon" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 2.5 20 6v5.2c0 5-3.4 8.7-8 10.3-4.6-1.6-8-5.3-8-10.3V6l8-3.5Z" />
            <path d="m8.8 11.7 2.2 2.2 4.5-4.5" />
          </svg>
          <h2 id="change-password-title">Cambiar contraseña</h2>
        </div>

        <form id="change-password-form" @submit.prevent="submit">
          <div class="form-item password-field">
            <label for="currentPassword">Contraseña actual</label>
            <el-input id="currentPassword" v-model="currentPassword"
              :type="showCurrentPassword ? 'text' : 'password'" name="currentPassword"
              placeholder="Ingresa tu contraseña actual" autocomplete="current-password" class="w-100">
              <template #suffix>
                <button class="password-toggle" type="button"
                  :aria-label="showCurrentPassword ? 'Ocultar contraseña actual' : 'Mostrar contraseña actual'"
                  @click="showCurrentPassword = !showCurrentPassword">
                  <font-awesome-icon :icon="showCurrentPassword ? 'eye-slash' : 'eye'" />
                </button>
              </template>
            </el-input>
          </div>

          <div class="form-item password-field">
            <label for="password">Nueva contraseña</label>
            <el-input id="password" v-model="password" :type="showPassword ? 'text' : 'password'" name="password"
              placeholder="Ingresa tu nueva contraseña" autocomplete="new-password" class="w-100"
              :class="{ 'is-invalid': submitted && passwordErrors.length }"
              :aria-invalid="submitted && passwordErrors.length > 0"
              :aria-describedby="submitted && passwordErrors.length ? 'password-hint password-errors' : 'password-hint'">
              <template #suffix>
                <button class="password-toggle" type="button"
                  :aria-label="showPassword ? 'Ocultar nueva contraseña' : 'Mostrar nueva contraseña'"
                  @click="showPassword = !showPassword">
                  <font-awesome-icon :icon="showPassword ? 'eye-slash' : 'eye'" />
                </button>
              </template>
            </el-input>
            <p id="password-hint" class="password-hint">
              Mínimo 12 caracteres, con al menos una mayúscula, un número y un símbolo.
            </p>
            <div v-if="submitted && passwordErrors.length" id="password-errors" class="invalid-feedback" role="alert">
              <div v-for="error in passwordErrors" :key="error">{{ error }}</div>
            </div>
          </div>

          <div class="form-item password-field">
            <label for="repeatPassword">Confirmar nueva contraseña</label>
            <el-input id="repeatPassword" v-model="repeatPassword" :type="showRepeatPassword ? 'text' : 'password'"
              name="repeatPassword" placeholder="Confirma tu nueva contraseña" autocomplete="new-password" class="w-100"
              :class="{ 'is-invalid': submitted && !samePassword(password, repeatPassword) }">
              <template #suffix>
                <button class="password-toggle" type="button"
                  :aria-label="showRepeatPassword ? 'Ocultar confirmación de contraseña' : 'Mostrar confirmación de contraseña'"
                  @click="showRepeatPassword = !showRepeatPassword">
                  <font-awesome-icon :icon="showRepeatPassword ? 'eye-slash' : 'eye'" />
                </button>
              </template>
            </el-input>
            <div v-if="submitted && !samePassword(password, repeatPassword)" class="animated fadeIn invalid-feedback">
              <font-awesome-icon class="icon" icon="info-circle" />
              Las contraseñas deben ser iguales
            </div>
          </div>

          <div class="change-password-card__actions">
            <button class="btn btn_red" type="submit">GUARDAR CAMBIOS</button>
            <button class="btn btn_yellow" type="button" @click="logout">CERRAR SESIÓN</button>
          </div>
        </form>
      </article>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.account-security {
  margin-bottom: 1.25rem;
}

.change-password-card__icon {
  fill: none;
  stroke: currentColor;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 1.8;
}

.change-password-card {
  background: #fff;
  border: 1px solid rgba(71, 76, 230, 0.1);
  border-radius: 0.75rem;
  box-shadow: 0 10px 24px rgba(51, 57, 126, 0.12);
  margin-left: 2rem;
  max-width: 33.5rem;
  padding: 1.75rem;
}

.change-password-card__heading {
  align-items: center;
  color: #474ce6;
  display: flex;
  gap: 0.55rem;
  margin-bottom: 1.45rem;
}

.change-password-card__icon {
  height: 1.7rem;
  width: 1.7rem;
}

.change-password-card h2 {
  font-size: 1.35rem;
  font-weight: 800;
  letter-spacing: -0.04em;
  margin: 0;
}

.password-field {
  margin-bottom: 1.1rem;
}

.password-hint {
  color: #626a7c;
  font-size: 0.85rem;
  margin: 0.35rem 0 0;
}

.password-field label {
  color: #303449;
  display: block;
  font-weight: 700;
  margin-bottom: 0.45rem;
}

.password-field :deep(.el-input__wrapper) {
  background: #fff;
  border: none !important;
  border-radius: 0.6rem;
  box-shadow: 0 0 0 1px #000 inset !important;
  height: 2.8rem !important;
  min-height: 2.8rem;
  padding: 1px 0.8rem 1px 1rem;
  transition: none;
}

.password-field :deep(.el-input__wrapper:hover),
.password-field :deep(.el-input__wrapper.is-focus) {
  box-shadow: 0 0 0 1px #409eff inset !important;
}

.password-field :deep(.el-input__inner) {
  background: transparent !important;
  border: 0 !important;
  border-radius: 0 !important;
  box-shadow: none !important;
  color: #303449;
  font-size: 1rem;
  height: 100% !important;
  margin: 0 !important;
  min-height: 0 !important;
  padding: 0 !important;
  transition: none;
}

.password-field :deep(.el-input__inner::placeholder) {
  color: #a6abb7;
}

// Extender el color de autocompletado al área del icono y conservar el contorno.
.password-field :deep(.el-input__wrapper:has(.el-input__inner:-webkit-autofill)) {
  background: #e8f0fe;
}

.password-field :deep(.el-input__wrapper:has(.el-input__inner:-moz-autofill)) {
  background: #e8f0fe;
}

.password-field :deep(.el-input__inner:-webkit-autofill),
.password-field :deep(.el-input__inner:-webkit-autofill:hover),
.password-field :deep(.el-input__inner:-webkit-autofill:focus) {
  -webkit-box-shadow: 0 0 0 1000px #e8f0fe inset !important;
}

.password-field :deep(.el-input__inner:-moz-autofill),
.password-field :deep(.el-input__inner:-moz-autofill:hover),
.password-field :deep(.el-input__inner:-moz-autofill:focus) {
  box-shadow: 0 0 0 1000px #e8f0fe inset !important;
}

.password-toggle {
  align-items: center;
  background: transparent;
  border: 0;
  color: #9aa1ad;
  cursor: pointer;
  display: inline-flex;
  font-size: 1rem;
  padding: 0.25rem;
}

.password-toggle:hover,
.password-toggle:focus-visible {
  color: #474ce6;
  outline: none;
}

.password-field :deep(.is-invalid .el-input__wrapper) {
  border: none !important;
  box-shadow: 0 0 0 1px #dc626f inset !important;
}

.change-password-card__actions {
  display: flex;
  gap: 0.85rem;
  margin-top: 1.55rem;
}

.change-password-card__actions .btn {
  align-items: center;
  border: 0;
  border-radius: 1.5rem;
  display: inline-flex;
  flex: 1 1 0;
  font-weight: 800;
  justify-content: center;
  min-height: 2.8rem;
  padding: 0.65rem 0.9rem;
  white-space: nowrap;
}

@media (max-width: 767px) {
  .change-password-card {
    margin: 0 0.75rem;
    max-width: none;
    padding: 1.35rem;
  }

  .change-password-card__actions {
    flex-direction: column;
  }

  .change-password-card__actions .btn {
    flex-basis: auto;
    width: 100%;
  }
}
</style>
